import { getErrorAction } from './errorMapper'
import uniqueId from 'lodash/uniqueId'
import { actions } from 'react-redux-toastr'
import moment from 'moment'
import { createReducer } from './reducerUtils'
import { mergeMap, map, catchError } from 'rxjs/operators'
import { ofType } from 'redux-observable'
import { of } from 'rxjs'

// Actions
export const COMMENT_ADD = 'COMMENT_ADD'
export const COMMENT_ADD_SUCCESS = 'COMMENT_ADD_SUCCESS'
export const COMMENT_ADD_FAILURE = 'COMMENT_ADD_FAILURE'

export const COMMENT_DELETE = 'COMMENT_DELETE'
export const COMMENT_DELETE_SUCCESS = 'COMMENT_DELETE_SUCCESS'
export const COMMENT_DELETE_FAILURE = 'COMMENT_DELETE_FAILURE'

export const COMMENT_MAP = 'COMMENT_MAP'

export const COMMENTS_LOAD = 'COMMENTS_LOAD'
export const COMMENTS_LOAD_SUCCESS = 'COMMENTS_LOAD_SUCCESS'
export const COMMENTS_LOAD_FAILURE = 'COMMENTS_LOAD_FAILURE'

export const GENERATED_COMMENT_ID_PREFIX = 'unsaved-comment-'

// Action creators
export const commentAdd = (taskId, commentText) => ({
  type: COMMENT_ADD,
  payload: {
    taskId,
    commentText: commentText.trim(),
    tempCommentId: uniqueId(GENERATED_COMMENT_ID_PREFIX)
  }
})
export const commentAddSuccess = (task, tempCommentId, comment, userId) => ({
  type: COMMENT_ADD_SUCCESS,
  payload: { task, tempCommentId, comment, userId }
})
export const commentAddFailure = payload => ({ type: COMMENT_ADD_FAILURE, payload, error: true })

export const commentDelete = (taskId, commentId) => ({
  type: COMMENT_DELETE,
  payload: { taskId, commentId }
})
export const commentDeleteSuccess = () => ({
  type: COMMENT_DELETE_SUCCESS
})
export const commentDeleteFailure = payload => ({ type: COMMENT_DELETE_FAILURE, payload, error: true })

export const commentMap = (taskId, comment) => ({ type: COMMENT_MAP, payload: { taskId, comment } })

export const commentsLoad = (taskId) => ({ type: COMMENTS_LOAD, payload: { taskId } })
export const commentsLoadSuccess = (task, userId, comments) => ({
  type: COMMENTS_LOAD_SUCCESS,
  payload: { task, userId, comments }
})
export const commentsLoadFailure = payload => ({ type: COMMENTS_LOAD_FAILURE, payload, error: true })

// Epics

export const commentDeleteEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(COMMENT_DELETE),
    mergeMap((action) => {
      const task = state$.value.tasks[action.payload.taskId]
      return vapiClient
        .deleteComment(action.payload.taskId, action.payload.commentId).pipe(
          map(() => commentDeleteSuccess()),
          catchError(error => of(getErrorAction(error, commentDeleteFailure, { task, commentId: action.payload.commentId })))
        )
    })
  )

export const commentDeleteSuccessEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(COMMENT_DELETE_SUCCESS),
    map((action) => {
      const commentDeleteSuccessToast = {
        type: 'success',
        message: localize(state$.value.resources, 'taskBoards.Msg_CommentDeleted'),
        options: {
          removeOnHover: true
        }
      }

      return actions.add(commentDeleteSuccessToast)
    })
  )

export const commentDeleteFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(COMMENT_DELETE_FAILURE),
    mergeMap((action) => {
      const task = action.payload.task
      let commentDeleteErrorMessage = localize(state$.value.resources, 'taskBoards.Msg_CommentDeleteFailed')
      commentDeleteErrorMessage = commentDeleteErrorMessage.replace('{taskName}', task.name)
      const commentDeleteFailureToast = {
        type: 'error',
        message: commentDeleteErrorMessage,
        options: {
          removeOnHover: true
        }
      }
      return [
        actions.add(commentDeleteFailureToast),
        commentsLoad(task.id)
      ]
    })
  )

export const commentAddEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(COMMENT_ADD),
    mergeMap((action) => {
      const task = state$.value.tasks[action.payload.taskId]
      const userId = state$.value.userInfo.id
      return vapiClient
        .addComment(action.payload.taskId, action.payload.commentText).pipe(
          map((ajaxResponse) => commentAddSuccess(task, action.payload.tempCommentId, ajaxResponse.response, userId)),
          catchError(error => of(getErrorAction(error, commentAddFailure, { task, tempCommentId: action.payload.tempCommentId })))
        )
    })
  )

export const commentAddFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(COMMENT_ADD_FAILURE),
    map((action) => {
    const taskName = action.payload.task.name
    const commentAddFailureToast = {
      type: 'error',
      message: localize(state$.value.resources, 'taskBoards.Msg_UnableToSaveComment')
        .replace('{taskName}', taskName),
      options: {
        removeOnHover: true
      }
    }
    return actions.add(commentAddFailureToast)
  })
)

export const commentMapEpic = (action$, state$) =>
  action$.pipe(
    ofType(COMMENT_ADD),
    map((action) => commentMap(
      action.payload.taskId,
      {
        id: action.payload.tempCommentId,
        text: action.payload.commentText,
        createdBy: state$.value.userInfo.id,
        createdOn: moment.utc().toISOString(),
        actions: {}
      }
    ))
  )

export const commentLoadEpic = (action$, state$, { vapiClient }) =>
  action$.pipe(
    ofType(COMMENTS_LOAD),
    mergeMap((action) =>
      vapiClient
        .getComments(action.payload.taskId).pipe(
          map((ajaxResponse) => commentsLoadSuccess(state$.value.tasks[action.payload.taskId], state$.value.userInfo.id, ajaxResponse.response)),
          catchError(error => of(getErrorAction(error, commentsLoadFailure, action.payload)))
        )
    )
  )

export const commentLoadFailureEpic = (action$, state$, { localize }) =>
  action$.pipe(
    ofType(COMMENTS_LOAD_FAILURE),
    map((action) => {
      const taskName = state$.value.tasks[action.payload.taskId].name
      const commentLoadFailureToast = {
        type: 'error',
        message: localize(state$.value.resources, 'taskBoards.Msg_UnableToLoadComments')
          .replace('{taskName}', taskName),
        options: {
          removeOnHover: true
        }
      }
      return actions.add(commentLoadFailureToast)
    })
  )

const mapComment = (comment, task, userId) => {
  let mappedComment = {
    id: comment.id,
    text: comment.commentText,
    createdBy: comment.createdBy,
    createdOn: comment.createdOn,
    actions: {}
  }

  if (task && userId) {
    if (task.actions.delete || comment.createdBy === userId) {
      mappedComment.actions = { delete: true }
    }
  }

  return mappedComment
}

const setComments = (state, taskId, comments) => ({
  ...state,
  [taskId]: comments
})

const commentAddSuccessReducer = (state, { comment, task, tempCommentId, userId }) => {
  const newComments = state[task.id].filter(item => item.id !== tempCommentId)
  return setComments(state, task.id, [ ...newComments, mapComment(comment, task, userId)])
}

const commentAddFailureReducer = (state, { task, tempCommentId }) => {
  let newComments = state[task.id].filter(comment => comment.id !== tempCommentId)
  return setComments(state, task.id, newComments)
}

const commentLoadSuccessReducer = (state, { comments, task, userId }) => {
  let newComments = comments.map(comment => mapComment(comment, task, userId))
  return setComments(state, task.id, newComments)
}

const commentLoadFailureReducer = (state, { taskId }) => {
  return setComments(state, taskId, [])
}

const commentMapReducer = (state, { taskId, comment }) => {
  let newComments = [ ...(state[taskId] || []), comment ]
  return setComments(state, taskId, newComments)
}

const commentDeleteReducer = (state, { commentId, taskId }) => {
  const newComments = state[taskId].filter(comment => comment.id !== commentId)
  return setComments(state, taskId, newComments)
}

const reducerHandlerMapping = {
  [COMMENT_ADD_SUCCESS]: commentAddSuccessReducer,
  [COMMENT_ADD_FAILURE]: commentAddFailureReducer,
  [COMMENTS_LOAD_SUCCESS]: commentLoadSuccessReducer,
  [COMMENTS_LOAD_FAILURE]: commentLoadFailureReducer,
  [COMMENT_MAP]: commentMapReducer,
  [COMMENT_DELETE]: commentDeleteReducer
}

// Reducers
const commentsInitialState = {}
export const commentsReducer = createReducer(commentsInitialState, reducerHandlerMapping)

export default commentsReducer



// WEBPACK FOOTER //
// ./src/store/comments.js